import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import axios from 'axios';
import UserRowSkeleton from '../components/Skeleton/UserRowSkeleton';
import Swal from "sweetalert2";
import formatCurrency from '../helper/formatCurrency';
import { useAuth } from '../context/AuthContext';
import { Pencil, Eye, Copy } from 'lucide-react';

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [debitAmounts, setDebitAmounts] = useState<{ [key: string]: string }>({});
  const [creditAmounts, setCreditAmounts] = useState<{ [key: string]: string }>({});

  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    uid: '',
    username: '',
    password: '',
    withdrawal_password: '',
    referred_by_id: '',
    avatar: '',
    is_blocked: false,
    refer_bounce_reward: true,
    cashback_bounce_reward: true,
    cashback_percent: '0',
    deposit_turnover: '',
    email: '',
    whatsapp: '',
    calling_number: '',
  });
  const [allUsersList, setAllUsersList] = useState<any[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    refree: '',
    refree_id: '',
    backoffice_id: '',
    agent_id: '',
    amount: '',
    whatsapp: '',
    calling_number: '',
  });
  const [backoffices, setBackoffices] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);

  useEffect(() => {
    fetchUsersData();
  }, [page, statusFilter]);

  useEffect(() => {
    if (user) {
      fetchBackofficesAndAgents();
      setCreateUserData((prev) => ({
        ...prev,
        backoffice_id: user.role === "backoffice" ? String(user.id) : prev.backoffice_id,
        agent_id: user.role === "agent" ? String(user.id) : prev.agent_id,
      }));
    }
  }, [user]);

  const fetchBackofficesAndAgents = async () => {
    try {
      if (user?.role === "admin") {
        const boRes = await api.get('/list');
        setBackoffices(boRes?.data?.data || []);
      }

      if (user?.role === "admin" || user?.role === "backoffice") {
        const agentRes = await api.get('/agent/list');
        setAgents(agentRes?.data?.data || []);
      }
    } catch (err) {
      console.error("Error fetching admin lists:", err);
    }
  };

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/getUserData', {
        params: {
          page,
          limit,
          search: searchTerm.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          status: statusFilter || undefined,
        },
      });

      setUsers(res.data.users || []);
      setTotalUsers(res.data.totalUsers);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsersList = async () => {
    try {
      const res = await api.get('/getUserData', {
        params: { page: 1, limit: 1000 },
      });
      setAllUsersList(res.data.users || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAllUsersList();
  }, []);

  const handleAmountChange = (uid: string, value: string, type: 'credit' | 'debit') => {
    if (type === "credit") {
      setCreditAmounts({ ...creditAmounts, [uid]: value });
    } else {
      setDebitAmounts({ ...debitAmounts, [uid]: value });
    }
  };

  const handleCreditOrDebit = async (uid: string, type: 'credit' | 'debit') => {
    const amount = type === "credit" ? creditAmounts[uid] : debitAmounts[uid];

    if (!amount || isNaN(Number(amount))) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Please enter a valid numeric amount.",
      });
      return;
    }

    try {
      const response = await api.post("/adminUpdatePlayerCredit", {
        id: uid,
        amount,
        type,
      });

      Swal.fire({
        icon: "success",
        title: "Wallet Updated",
        text: response.data.message || `Player ${type} successful.`,
      });

      fetchUsersData();
      setDebitAmounts({ ...debitAmounts, [uid]: "" });
      setCreditAmounts({ ...creditAmounts, [uid]: "" });
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "Failed to update wallet.",
      });
    }
  };

  const handleDeleteUser = async (id: number, name: string) => {
    return
    await api.delete('/deleteUser', { data: { id } });
    setSnackbar(`User ${name} blocked`);
    fetchUsersData();
    setTimeout(() => setSnackbar(null), 3000);
  };

  const handleOpenModal = (user: any) => {
    setEditUserData({
      uid: String(user.id),
      username: user.name,
      password: '',
      withdrawal_password: user.withdrawal_password || '',
      referred_by_id: user.referred_by_id ? String(user.referred_by_id) : '',
      avatar: user.avatar || '',
      is_blocked: !!user.is_blocked,
      refer_bounce_reward: user.refer_bounce_reward !== false,
      cashback_bounce_reward: user.cashback_bounce_reward !== false,
      cashback_percent: String(user.cashback_percent || '0'),
      deposit_turnover: user.deposit_turnover !== null && user.deposit_turnover !== undefined ? String(user.deposit_turnover) : '',
      email: user.email || '',
      whatsapp: user.whatsapp || '',
      calling_number: user.calling_number || user.phone || '',
    });
    setIsModalOpen(true);
  };

  const handleEditUser = async () => {
    try {
      await api.put('/editProfile', {
        uid: editUserData.uid,
        username: editUserData.username,
        password: editUserData.password || undefined,
        withdrawal_password: editUserData.withdrawal_password || undefined,
        referred_by_id: editUserData.referred_by_id ? Number(editUserData.referred_by_id) : null,
        selectedImage: editUserData.avatar || null,
        is_blocked: editUserData.is_blocked,
        refer_bounce_reward: editUserData.refer_bounce_reward,
        cashback_bounce_reward: editUserData.cashback_bounce_reward,
        cashback_percent: Number(editUserData.cashback_percent),
        deposit_turnover: editUserData.deposit_turnover,
        email: editUserData.email || undefined,
        whatsapp: editUserData.whatsapp || undefined,
        calling_number: editUserData.calling_number || undefined,
      });

      Swal.fire({
        icon: "success",
        title: "User Updated",
        text: "User details have been successfully updated.",
      });
      fetchUsersData();
      setIsModalOpen(false);
    } catch (err: any) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Update User",
        text: err.response?.data?.error || "Failed to update user settings.",
      });
    }
  };

  const handleBackofficeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const boId = e.target.value;
    setCreateUserData((prev) => ({
      ...prev,
      backoffice_id: boId,
      agent_id: "",
      refree_id: "",
    }));
  };

  const handleAgentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const agentId = e.target.value;
    setCreateUserData((prev) => ({
      ...prev,
      agent_id: agentId,
      refree_id: "",
    }));
  };

  const handleCreatePlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createUserData.password) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Password is required' });
      return;
    }
    if (createUserData.password !== createUserData.confirm_password) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Passwords do not match' });
      return;
    }
    try {
      await api.post('/auth/admin-register', {
        username: createUserData.username || undefined,
        email: createUserData.email || undefined,
        password: createUserData.password,
        phone: createUserData.phone || undefined,
        refree_id: createUserData.refree_id || undefined,
        backoffice_id: createUserData.backoffice_id || undefined,
        agent_id: createUserData.agent_id || undefined,
        amount: createUserData.amount ? Number(createUserData.amount) : 0,
        whatsapp: createUserData.whatsapp || undefined,
        calling_number: createUserData.calling_number || undefined,
      });

      Swal.fire({
        icon: "success",
        title: "Player Created",
        text: "Player account has been successfully created.",
      });

      setIsCreateModalOpen(false);
      setCreateUserData({
        username: '',
        email: '',
        password: '',
        confirm_password: '',
        phone: '',
        refree: '',
        refree_id: '',
        backoffice_id: user?.role === "backoffice" ? String(user.id) : '',
        agent_id: user?.role === "agent" ? String(user.id) : '',
        amount: '',
        whatsapp: '',
        calling_number: '',
      });
      fetchUsersData();
    } catch (err: any) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Create Player",
        text: err.response?.data?.message || "Failed to create player.",
      });
    }
  };



  const handleBlockUnBlockUser = async (uid: string, isBlocked: boolean) => {
    let block_reason = null;

    // If blocking → ask for reason
    if (!isBlocked) {
      const result = await Swal.fire({
        title: "Block User",
        input: "textarea",
        inputLabel: "Reason for blocking",
        inputPlaceholder: "Enter block reason...",
        inputAttributes: {
          maxlength: 255,
        },
        showCancelButton: true,
        confirmButtonText: "Block",
        confirmButtonColor: "#d33",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) {
            return "Block reason is required";
          }
        },
      });

      if (!result.isConfirmed) return;

      block_reason = result.value;
    }

    await api.put("/blockUser", {
      uid,
      is_blocked: !isBlocked,
      block_reason,
    });

    fetchUsersData();
    setIsModalOpen(false);
    Swal.fire({
      icon: "success",
      title: `User ${isBlocked ? "unblocked" : "blocked"}`,
      text: `User has been successfully ${isBlocked ? "unblocked" : "blocked"}.`,
    });

    setTimeout(() => setSnackbar(null), 3000);
  };

  const handleMakeCollaborator = async (uid, isCollaborator) => {
    try {

      //are you sure to make collaborator
      const result = await Swal.fire({
        title: "Make Collaborator",
        text: "Are you sure you want to make this user a collaborator?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (!result.isConfirmed) return;

      await api.put("/makeCollaborator", {
        uid,
        is_collaborator: !isCollaborator,
      });

      fetchUsersData();
      setIsModalOpen(false);
      setSnackbar("User status updated");

      setTimeout(() => setSnackbar(null), 3000);
    } catch (err) {
      console.log(err)
    }
  };


  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar(`Copied "${text}" to clipboard!`);
    setTimeout(() => setSnackbar(null), 2000);
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(u.id).includes(searchTerm)
  );

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between mb-6">
        {/* <div>
          <h1 className="text-2xl font-bold text-gray-800">
            User Management
          </h1>
          <p className="text-sm text-gray-500">
            Manage user accounts and their settings.
          </p>
        </div> */}
        <div className="flex items-center gap-4">


        </div>
      </div>

      {snackbar && (
        <div className="mb-4 rounded bg-green-100 text-green-800 px-4 py-2">
          {snackbar}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-4 rounded-xl border">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search</label>
          <input
            className="rounded border px-4 py-1.5 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-green-500"
            placeholder="Search by Name or User ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                fetchUsersData();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From Date</label>
          <input
            type="date"
            className="rounded border px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To Date</label>
          <input
            type="date"
            className="rounded border px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Status</label>
          <select
            className="rounded border px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 bg-white"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>

        <div className="flex items-end gap-2 h-14 pt-5">
          <button
            onClick={() => {
              setPage(1);
              fetchUsersData();
            }}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold text-sm transition"
          >
            Search
          </button>
          <button
            onClick={() => {
              setSearchTerm('');
              setStartDate('');
              setEndDate('');
              setStatusFilter('');
              setPage(1);
              setLoading(true);
              api.get('/getUserData', {
                params: { page: 1, limit },
              }).then((res) => {
                setUsers(res.data.users || []);
                setTotalUsers(res.data.totalUsers);
                setTotalPages(res.data.totalPages);
              }).catch(console.error).finally(() => setLoading(false));
            }}
            className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-semibold text-sm transition"
          >
            Clear
          </button>
        </div>
        <div className="w-full"></div>
        <button
          onClick={() => {
            setCreateUserData({
              username: '',
              email: '',
              password: '',
              confirm_password: '',
              phone: '',
              refree: '',
              refree_id: '',
              backoffice_id: user?.role === "backoffice" ? String(user.id) : '',
              agent_id: user?.role === "agent" ? String(user.id) : '',
            });
            setIsCreateModalOpen(true);
          }}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition"
        >
          + Create Player
        </button>

      </div>

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm truncate">
          <thead className="bg-gray-100 ">
            <tr>
              {[
                'S.No',
                'Player Name',
                'Password',
                'Upline Agent',
                'Upline Branch',
                'Referrer',
                'Main Wallet',
                'Exposure',
                'Cashback Wallet',
                'Refer Bounce Wallet',
                'Account Status',
                'Created Data',
                'Last Login Date/IP',
                'Withdrawal',
                'Deposit',
                'Action',

              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <UserRowSkeleton columns={12} rows={10} />
            ) : (
              users.map((user: any, i) => {
                const deposit = user.actual_total_deposit_amount || 0;
                const withdrawal = user.actual_total_withdrawal_amount || 0;
                const balance = user.available_inr || 0;

                return (
                  <tr key={user.id} className="border-t">
                    <td className="px-4 py-2">{(page - 1) * limit + i + 1}</td>
                    <td className="px-4 py-2 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{user.name} ({user.id})</span>
                        <button
                          onClick={() => handleCopyText(user.name)}
                          className="text-gray-400 hover:text-blue-600 transition"
                          title="Copy Player Name"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2 font-medium">{user.password2}</td>
                    <td className="px-4 py-2 font-medium">{user.agent_name || 'N/A'}</td>
                    <td className="px-4 py-2 font-medium">{user.backoffice_name || 'N/A'}</td>
                    <td className="px-4 py-2 font-medium">{user.referee_name}</td>
                    <td
                      className={`px-4 py-2 font-semibold ${balance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                    >
                      {formatCurrency(balance)}
                    </td>

                    <td className="px-4 py-2">{user?.exposure !== null && user?.exposure !== undefined && user?.exposure !== 'N/A' ? formatCurrency(Number(user.exposure)) : 'N/A'}</td>
                    <td className="px-4 py-2">{user?.cashback_amount !== null && user?.cashback_amount !== undefined ? formatCurrency(Number(user.cashback_amount)) : 'N/A'}</td>
                    <td className="px-4 py-2">{user?.referral_bounce_amount !== null && user?.referral_bounce_amount !== undefined ? formatCurrency(Number(user.referral_bounce_amount)) : 'N/A'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded ${user?.is_blocked ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>{user?.is_blocked ? "Blocked" : "Active"}</span></td>
                    <td className="px-4 py-2">{new Date(user.created).toLocaleString()}</td>
                    <td className="px-4 py-2">{new Date(user.last_login).toLocaleString() + "|" + user?.ip_address}</td>

                    {/* Debit */}
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center -space-x-px">
                        <input
                          type="text"
                          value={debitAmounts[user.id] || ""}
                          onChange={(e) =>
                            handleAmountChange(user.id, e.target.value, "debit")
                          }
                          placeholder="Amount"
                          className="p-1.5 border rounded-l-lg w-20 text-xs text-center focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                        <button
                          onClick={() => handleCreditOrDebit(user.id, "debit")}
                          className="bg-red-500 text-white text-xs py-1.5 px-3 rounded-r-lg hover:bg-red-600 transition font-semibold"
                        >
                          Withdrawal
                        </button>
                      </div>
                    </td>

                    {/* Credit */}
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center -space-x-px">
                        <input
                          type="text"
                          value={creditAmounts[user.id] || ""}
                          onChange={(e) =>
                            handleAmountChange(user.id, e.target.value, "credit")
                          }
                          placeholder="Amount"
                          className="p-1.5 border rounded-l-lg w-20 text-xs text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                        <button
                          onClick={() => handleCreditOrDebit(user.id, "credit")}
                          className="bg-green-600 text-white text-xs py-1.5 px-3 rounded-r-lg hover:bg-green-700 transition font-semibold"
                        >
                          Deposit
                        </button>
                      </div>
                    </td>

                    <td className="px-4 py-2 space-x-2 flex justify-between items-center">
                      <button
                        onClick={() => handleOpenModal(user)}
                        className="border p-1.5 rounded hover:bg-gray-100 transition text-blue-600 border-blue-200"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/users/profile/${user.id}`)}
                        className="border border-green-300 text-green-600 p-1.5 rounded hover:bg-green-50 transition"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* <button
                        onClick={() => handleBlockUnBlockUser(user.id, user.is_blocked)}
                        className={`border ${user.is_blocked ? "border-yellow-300 text-yellow-600" : "border-red-300 text-red-600"} px-3 py-1 rounded`}
                      >
                        {user.is_blocked ? "Unblock" : "Block"}
                      </button> */}
                      {/* make agent button */}
                      {/* <button
                        onClick={() => handleMakeCollaborator(user.id, user.is_collaborator)}
                        className={`border ${user.is_collaborator ? "border-yellow-300 text-yellow-600" : "border-red-300 text-red-600"} px-3 py-1 rounded`}
                      >
                        {user.is_collaborator ? "Collaborator" : "Make Collaborator"}
                      </button> */}

                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span className="px-3 py-1">
          {page} / {totalPages}
        </span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Edit Modal (Player Settings UI) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg border w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-lg font-bold text-gray-800">Player Setting</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Username / Player Name (Editable) */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Player Name</span>
                <input
                  type="text"
                  className="w-2/3 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.username}
                  onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
                />
              </div>

              {/* Email (Optional) */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Email</span>
                <input
                  type="email"
                  className="w-2/3 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                  placeholder="e.g. member@email.com (Optional)"
                />
              </div>

              {/* WhatsApp (Optional) */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">WhatsApp</span>
                <input
                  type="text"
                  className="w-2/3 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.whatsapp}
                  onChange={(e) => setEditUserData({ ...editUserData, whatsapp: e.target.value })}
                  placeholder="e.g. +919876543210 (Optional)"
                />
              </div>

              {/* Calling Number (Optional) */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Calling Number</span>
                <input
                  type="text"
                  className="w-2/3 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.calling_number}
                  onChange={(e) => setEditUserData({ ...editUserData, calling_number: e.target.value })}
                  placeholder="e.g. +919876543210 (Optional)"
                />
              </div>

              {/* Referrer Selector */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Referrer</span>
                <select
                  className="w-2/3 border rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.referred_by_id}
                  onChange={(e) => setEditUserData({ ...editUserData, referred_by_id: e.target.value })}
                >
                  <option value="">Please Select Player Username</option>
                  {allUsersList
                    .filter((u) => String(u.id) !== editUserData.uid) // avoid self-referral
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Avatar Selector */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Avatar</span>
                <select
                  className="w-2/3 border rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.avatar}
                  onChange={(e) => setEditUserData({ ...editUserData, avatar: e.target.value })}
                >
                  <option value="">Select Avatar</option>
                  <option value="https://api.jeetoo.bet/uploads/avatar1.png">Avatar 1</option>
                  <option value="https://api.jeetoo.bet/uploads/avatar2.png">Avatar 2</option>
                  <option value="https://api.jeetoo.bet/uploads/avatar3.png">Avatar 3</option>
                  <option value="https://api.jeetoo.bet/uploads/avatar4.png">Avatar 4</option>
                  <option value="https://api.jeetoo.bet/uploads/avatar5.png">Avatar 5</option>
                </select>
              </div>

              {/* Status Switch */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Status</span>
                <div className="w-2/3 flex items-center">
                  <div
                    onClick={() => setEditUserData({ ...editUserData, is_blocked: !editUserData.is_blocked })}
                    className={`w-20 h-8 rounded flex items-center p-1 cursor-pointer transition-colors duration-300 ${!editUserData.is_blocked ? 'bg-green-600 justify-end' : 'bg-gray-500 justify-start'
                      }`}
                  >
                    {!editUserData.is_blocked ? (
                      <>
                        <span className="text-white text-xs font-bold pl-2 select-none mr-auto">ON</span>
                        <div className="w-8 h-6 bg-white rounded shadow-md"></div>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-6 bg-white rounded shadow-md"></div>
                        <span className="text-white text-xs font-bold pr-2 select-none ml-auto">OFF</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Refer Bounce Reward Switch */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Refer Bounce Reward</span>
                <div className="w-2/3 flex items-center">
                  <div
                    onClick={() => setEditUserData({ ...editUserData, refer_bounce_reward: !editUserData.refer_bounce_reward })}
                    className={`w-20 h-8 rounded flex items-center p-1 cursor-pointer transition-colors duration-300 ${editUserData.refer_bounce_reward ? 'bg-green-600 justify-end' : 'bg-gray-500 justify-start'
                      }`}
                  >
                    {editUserData.refer_bounce_reward ? (
                      <>
                        <span className="text-white text-xs font-bold pl-2 select-none mr-auto">ON</span>
                        <div className="w-8 h-6 bg-white rounded shadow-md"></div>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-6 bg-white rounded shadow-md"></div>
                        <span className="text-white text-xs font-bold pr-2 select-none ml-auto">OFF</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Cash Back Bounce Reward Switch */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Cash Back Bounce Reward</span>
                <div className="w-2/3 flex items-center">
                  <div
                    onClick={() => setEditUserData({ ...editUserData, cashback_bounce_reward: !editUserData.cashback_bounce_reward })}
                    className={`w-20 h-8 rounded flex items-center p-1 cursor-pointer transition-colors duration-300 ${editUserData.cashback_bounce_reward ? 'bg-green-600 justify-end' : 'bg-gray-500 justify-start'
                      }`}
                  >
                    {editUserData.cashback_bounce_reward ? (
                      <>
                        <span className="text-white text-xs font-bold pl-2 select-none mr-auto">ON</span>
                        <div className="w-8 h-6 bg-white rounded shadow-md"></div>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-6 bg-white rounded shadow-md"></div>
                        <span className="text-white text-xs font-bold pr-2 select-none ml-auto">OFF</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Cashback Percent Dropdown */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Cashback Percent (%)</span>
                <select
                  className="w-2/3 border rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                  value={editUserData.cashback_percent}
                  onChange={(e) => setEditUserData({ ...editUserData, cashback_percent: e.target.value })}
                >
                  <option value="0">Select</option>
                  <option value="1">1%</option>
                  <option value="2">2%</option>
                  <option value="3">3%</option>
                  <option value="4">4%</option>
                  <option value="5">5%</option>
                  <option value="6">6%</option>
                  <option value="7">7%</option>
                  <option value="8">8%</option>
                  <option value="9">9%</option>
                  <option value="10">10%</option>
                </select>
              </div>

              {/* Custom Turnover Factor */}
              <div className="flex items-center">
                <span className="w-1/3 font-bold text-gray-700">Custom Turnover</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  disabled={user?.role !== 'admin'}
                  placeholder={user?.role === 'admin' ? "e.g. 1.5 (Leave blank for global)" : "Only admins can edit"}
                  className={`w-2/3 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 ${user?.role !== 'admin' ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                    }`}
                  value={editUserData.deposit_turnover}
                  onChange={(e) => setEditUserData({ ...editUserData, deposit_turnover: e.target.value })}
                />
              </div>

              {/* Optional: Change Password inside modal */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="text-sm font-bold text-gray-800">Change Credentials (Optional)</h3>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">New Login Password</label>
                  <input
                    type="password"
                    placeholder="Enter new password (optional)"
                    autoComplete="new-password"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={editUserData.password}
                    onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })}
                  />
                </div>
                {/* <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">New Withdrawal Password</label>
                  <input
                    type="password"
                    placeholder="Enter new withdrawal password (optional)"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={editUserData.withdrawal_password || ''}
                    onChange={(e) => setEditUserData({ ...editUserData, withdrawal_password: e.target.value })}
                  />
                </div> */}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-semibold text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm transition"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Player Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-lg border overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Create Player</h2>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreatePlayer} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Modal Body (Scrollable) */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1 pr-6">
                {/* Dummy inputs to prevent browser autofill */}
                <input type="text" style={{ display: 'none' }} autoComplete="username" />
                <input type="password" style={{ display: 'none' }} autoComplete="new-password" />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    placeholder="e.g. Member123"
                    autoComplete="off"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.username}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, username: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
                  <input
                    type="email"
                    placeholder="e.g. member@email.com"
                    autoComplete="off"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.email}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, email: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. +919876543210"
                    autoComplete="off"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.whatsapp}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, whatsapp: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calling Number (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. +919876543210"
                    autoComplete="off"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.calling_number}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, calling_number: e.target.value, phone: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    placeholder="Enter secure password"
                    required
                    autoComplete="new-password"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.password}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, password: e.target.value })
                    }
                  />
                </div>

                {/* confirm_password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    placeholder="Confirm password"
                    required
                    autoComplete="new-password"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.confirm_password}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, confirm_password: e.target.value })
                    }
                  />
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. 919876543210"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.phone}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, phone: e.target.value })
                    }
                  />
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Initial Credit Amount</label>
                  <input
                    type="number"
                    placeholder="e.g. 500"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.amount}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, amount: e.target.value })
                    }
                  />
                </div>

                {user?.role === "admin" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Back Office (Optional)</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 bg-white"
                      value={createUserData.backoffice_id}
                      onChange={handleBackofficeChange}
                    >
                      <option value="">Select Back Office</option>
                      {backoffices.map((bo) => (
                        <option key={bo.id} value={bo.id}>
                          {bo.username}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(user?.role === "admin" || user?.role === "backoffice") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Agent (Optional)</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 bg-white"
                      value={createUserData.agent_id}
                      onChange={handleAgentChange}
                    >
                      <option value="">Select Agent</option>
                      {agents
                        .filter((agent) => {
                          if (createUserData.backoffice_id) {
                            return String(agent.parent_id) === String(createUserData.backoffice_id);
                          }
                          return user?.role === "admin";
                        })
                        .map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.username}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referee User (Optional)</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 bg-white"
                    value={createUserData.refree_id}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, refree_id: e.target.value })
                    }
                  >
                    <option value="">Select Referee Player</option>
                    {users
                      .filter((u) => {
                        if (createUserData.agent_id) {
                          return String(u.agent_id) === String(createUserData.agent_id);
                        }
                        if (createUserData.backoffice_id) {
                          return String(u.backoffice_id) === String(createUserData.backoffice_id);
                        }
                        return true;
                      })
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referrer Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="Referee referral code"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    value={createUserData.refree}
                    onChange={(e) =>
                      setCreateUserData({ ...createUserData, refree: e.target.value })
                    }
                  />
                </div> */}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-5 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-semibold text-sm transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
